import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import { Toast as RadixToast } from "radix-ui";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";

export type ToastTone = "success" | "error" | "info";
export type ToastInput = {
  title: string;
  description?: string;
  tone?: ToastTone;
  durationMs?: number;
};
type ToastItem = ToastInput & { id: number };

const ToastContext = createContext<((toast: ToastInput) => void) | null>(null);

const icons: Record<ToastTone, typeof Info> = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
};
const colors: Record<ToastTone, string> = {
  success: "text-success-strong",
  error: "text-danger",
  info: "text-accent",
};

// Mount once in the app root; `useToast()` anywhere below.
export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation("translation", { keyPrefix: "common" });
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((toast: ToastInput) => {
    setItems((current) => [...current.slice(-3), { ...toast, id: Date.now() + Math.random() }]);
  }, []);
  const value = useMemo(() => push, [push]);
  return (
    <ToastContext.Provider value={value}>
      <RadixToast.Provider swipeDirection="right" duration={4000} label={t("notifications")}>
        {children}
        {items.map((item) => {
          const Icon = icons[item.tone ?? "info"];
          return (
            <RadixToast.Root
              key={item.id}
              duration={item.durationMs}
              onOpenChange={(open) => {
                if (!open) setItems((current) => current.filter((i) => i.id !== item.id));
              }}
              className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-white p-4 shadow-lg data-[state=open]:animate-in"
            >
              <Icon
                className={cn("mt-0.5 size-5 shrink-0", colors[item.tone ?? "info"])}
                aria-hidden
              />
              <div className="flex-1">
                <RadixToast.Title className="font-medium text-zinc-900">
                  {item.title}
                </RadixToast.Title>
                {item.description ? (
                  <RadixToast.Description className="mt-0.5 text-sm text-zinc-600">
                    {item.description}
                  </RadixToast.Description>
                ) : null}
              </div>
              <RadixToast.Close
                aria-label={t("close")}
                className="-my-3 -mr-3 flex size-11 shrink-0 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100"
              >
                <X className="size-5" aria-hidden />
              </RadixToast.Close>
            </RadixToast.Root>
          );
        })}
        <RadixToast.Viewport className="fixed top-3 right-3 left-3 z-[60] flex flex-col gap-2 outline-none md:bottom-6 md:top-auto md:left-auto md:w-96" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const push = useContext(ToastContext);
  if (!push) throw new Error("useToast needs ToastProvider");
  return push;
}
